import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { iasApi } from '../config/api';

const JoumpaAirportSelect = ({ value, onChange, placeholder = 'Cari airport...' }) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('');

  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const fetchAirports = useCallback(async (searchText) => {
    try {
      setLoading(true);
      setError(null);
      const response = await iasApi.get('/joumpa/airport', {
        params: { search_text: searchText, page: 1, length: 20 },
      });
      const data = response?.data?.data;
      const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setOptions(rows);
      if (rows.length > 0) {
        setIsOpen(true);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Gagal memuat data airport');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearch(text);
    setSelectedLabel('');

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (text.trim().length >= 1) {
      debounceRef.current = setTimeout(() => {
        fetchAirports(text.trim());
      }, 300);
    } else {
      setOptions([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (airport) => {
    const code = airport?.code || airport?.airport_code || airport?.AIRPORT_CODE || '';
    const name = airport?.name || airport?.airport_name || airport?.AIRPORT_NAME || '';
    const city = airport?.city || airport?.airport_city || airport?.AIRPORT_CITY || '';
    const label = city ? `${code} - ${name} (${city})` : `${code} - ${name}`;
    setSearch('');
    setSelectedLabel(label);
    setIsOpen(false);
    setOptions([]);
    if (onChange) onChange(code);
  };

  // Reset internal state when external value changes from parent
  useEffect(() => {
    if (value != null && value !== '' && selectedLabel === '') {
      setSelectedLabel(value);
    }
    if (value == null || value === '') {
      setSelectedLabel('');
      setSearch('');
    }
  }, [value, selectedLabel]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box ref={containerRef} position="relative">
      <InputGroup size="md">
        <Input
          ref={inputRef}
          placeholder={selectedLabel || placeholder}
          value={search}
          onChange={handleSearchChange}
          onFocus={() => {
            if (options.length > 0) setIsOpen(true);
          }}
          autoComplete="off"
        />
        {loading && (
          <InputRightElement>
            <Spinner size="sm" color="blue.500" />
          </InputRightElement>
        )}
      </InputGroup>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          zIndex={1500}
          maxH="240px"
          overflowY="auto"
        >
          {error && (
            <Text fontSize="sm" color="red.500" p={3}>
              {error}
            </Text>
          )}
          {!error && options.length === 0 && !loading && (
            <Text fontSize="sm" color="gray.500" p={3}>
              Tidak ditemukan
            </Text>
          )}
          {!error &&
            options.map((item, idx) => {
              const code = item?.code || item?.airport_code || item?.AIRPORT_CODE || '';
              const name = item?.name || item?.airport_name || item?.AIRPORT_NAME || '';
              const city = item?.city || item?.airport_city || item?.AIRPORT_CITY || '';
              return (
                <VStack
                  key={code || idx}
                  align="stretch"
                  spacing={0}
                  cursor="pointer"
                  _hover={{ bg: 'blue.50' }}
                  px={3}
                  py={2}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  onClick={() => handleSelect(item)}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {code} - {name}
                  </Text>
                  {city && (
                    <Text fontSize="xs" color="gray.500">
                      {city}
                    </Text>
                  )}
                </VStack>
              );
            })}
        </Box>
      )}
    </Box>
  );
};

export default JoumpaAirportSelect;
